import cn from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import type { CropBox } from '../../config';

import styles from './CropOverlay.module.scss';

type HandlePosition =
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right';

type DragState = {
  type: 'move' | 'resize';
  handle?: HandlePosition;
  startX: number;
  startY: number;
  startBox: CropBox;
};

type CropOverlayProps = {
  className: string;
  containerWidth: number;
  containerHeight: number;
  cropBox: CropBox;
  onChange: (cropBox: CropBox) => void;
};

const MIN_SIZE = 0.05;

export const CropOverlay: FC<CropOverlayProps> = (props) => {
  const { className, containerWidth, containerHeight, cropBox, onChange } =
    props;

  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const clampBox = useCallback((box: CropBox): CropBox => {
    const width = Math.max(MIN_SIZE, Math.min(1, box.width));
    const height = Math.max(MIN_SIZE, Math.min(1, box.height));
    const x = Math.max(0, Math.min(1 - width, box.x));
    const y = Math.max(0, Math.min(1 - height, box.y));

    return { x, y, width, height };
  }, []);

  const handlePointerDown = useCallback(
    (
      event: React.PointerEvent,
      type: 'move' | 'resize',
      handle?: HandlePosition,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      (event.target as HTMLElement).setPointerCapture(event.pointerId);

      setDragState({
        type,
        handle,
        startX: event.clientX,
        startY: event.clientY,
        startBox: { ...cropBox },
      });
    },
    [cropBox],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!dragState || !containerWidth || !containerHeight) {
        return;
      }

      const dx = (event.clientX - dragState.startX) / containerWidth;
      const dy = (event.clientY - dragState.startY) / containerHeight;
      const { startBox } = dragState;

      if (dragState.type === 'move') {
        onChange(
          clampBox({
            ...startBox,
            x: startBox.x + dx,
            y: startBox.y + dy,
          }),
        );

        return;
      }

      const newBox = { ...startBox };

      switch (dragState.handle) {
        case 'topLeft':
          newBox.x = startBox.x + dx;
          newBox.y = startBox.y + dy;
          newBox.width = startBox.width - dx;
          newBox.height = startBox.height - dy;
          break;
        case 'topRight':
          newBox.y = startBox.y + dy;
          newBox.width = startBox.width + dx;
          newBox.height = startBox.height - dy;
          break;
        case 'bottomLeft':
          newBox.x = startBox.x + dx;
          newBox.width = startBox.width - dx;
          newBox.height = startBox.height + dy;
          break;
        case 'bottomRight':
          newBox.width = startBox.width + dx;
          newBox.height = startBox.height + dy;
          break;
        case 'top':
          newBox.y = startBox.y + dy;
          newBox.height = startBox.height - dy;
          break;
        case 'bottom':
          newBox.height = startBox.height + dy;
          break;
        case 'left':
          newBox.x = startBox.x + dx;
          newBox.width = startBox.width - dx;
          break;
        case 'right':
          newBox.width = startBox.width + dx;
          break;
      }

      onChange(clampBox(newBox));
    },
    [dragState, containerWidth, containerHeight, onChange, clampBox],
  );

  const handlePointerUp = useCallback(() => {
    setDragState(null);
  }, []);

  useEffect(() => {
    if (!dragState) {
      return;
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [dragState, handlePointerMove, handlePointerUp]);

  const boxStyle = {
    left: `${cropBox.x * 100}%`,
    top: `${cropBox.y * 100}%`,
    width: `${cropBox.width * 100}%`,
    height: `${cropBox.height * 100}%`,
  };

  const handles: HandlePosition[] = [
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
    'top',
    'bottom',
    'left',
    'right',
  ];

  return (
    <div
      ref={overlayRef}
      className={cn(styles.container, className)}
      data-component="photo-crop-overlay"
    >
      <div className={styles.dimOverlay}>
        <div
          className={styles.dimTop}
          style={{ height: `${cropBox.y * 100}%` }}
        />
        <div
          className={styles.dimMiddle}
          style={{ height: `${cropBox.height * 100}%` }}
        >
          <div
            className={styles.dimLeft}
            style={{ width: `${cropBox.x * 100}%` }}
          />
          <div
            className={styles.dimRight}
            style={{
              width: `${(1 - cropBox.x - cropBox.width) * 100}%`,
            }}
          />
        </div>
        <div
          className={styles.dimBottom}
          style={{
            height: `${(1 - cropBox.y - cropBox.height) * 100}%`,
          }}
        />
      </div>

      <div
        className={styles.cropBox}
        style={boxStyle}
        onPointerDown={(event) => handlePointerDown(event, 'move')}
      >
        {handles.map((handle) => (
          <div
            key={handle}
            className={cn(styles.handle, styles[handle])}
            onPointerDown={(event) =>
              handlePointerDown(event, 'resize', handle)
            }
          />
        ))}
      </div>
    </div>
  );
};
